import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Users, UserPlus, Shield, CheckCircle2, Mail, Phone, Calendar, Heart, Trash2, Edit2, CreditCard, Sparkles, ExternalLink, QrCode, UserCheck } from "lucide-react";
import MemberLayout from "@/layouts/MemberLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { useGetHousehold, useAddHouseholdMember, useUpdateHouseholdMember, useDeleteHouseholdMember } from "@/lib/api-client-react";

const RELATIONSHIPS = [
  "Spouse",
  "Child",
  "Parent",
  "Sibling",
  "Dependent",
  "Partner",
  "Other"
];

const GENDERS = ["Male", "Female", "Non-binary", "Prefer not to say"];

export default function MemberHousehold() {
  const { toast } = useToast();
  const { data: household, isLoading, refetch } = useGetHousehold();

  const addMemberMutation = useAddHouseholdMember();
  const updateMemberMutation = useUpdateHouseholdMember();
  const deleteMemberMutation = useDeleteHouseholdMember();

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedCardMember, setSelectedCardMember] = useState<any>(null);
  const [editingMember, setEditingMember] = useState<any>(null);

  // Add Member Form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [dob, setDob] = useState("");
  const [gender, setGender] = useState("");
  const [relationship, setRelationship] = useState("Spouse");
  const [accountType, setAccountType] = useState<'SEPARATE_ACCOUNT' | 'MANAGED_DEPENDENT'>('SEPARATE_ACCOUNT');

  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhone("");
    setDob("");
    setGender("");
    setRelationship("Spouse");
    setAccountType("SEPARATE_ACCOUNT");
    setEditingMember(null);
  };

  const handleAddSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim() || !relationship) {
      toast({
        title: "Validation Error",
        description: "First name, last name, and relationship are required.",
        variant: "destructive"
      });
      return;
    }

    if (accountType === 'SEPARATE_ACCOUNT' && !email.trim()) {
      toast({
        title: "Email Required",
        description: "Email address is required for separate account invitations.",
        variant: "destructive"
      });
      return;
    }

    addMemberMutation.mutate(
      {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        dob: dob.trim() || undefined,
        gender: gender.trim() || undefined,
        relationship,
        accountType
      },
      {
        onSuccess: () => {
          toast({
            title: "Member Added to Household",
            description: accountType === 'SEPARATE_ACCOUNT' && email
              ? `Household invitation sent to ${email}. They can register or log in with their own separate account.`
              : `${firstName} ${lastName} has been added to your household profile.`
          });
          setIsAddModalOpen(false);
          resetForm();
          refetch();
        },
        onError: (err: any) => {
          toast({
            title: "Failed to Add Member",
            description: err.message || "Could not add household member.",
            variant: "destructive"
          });
        }
      }
    );
  };

  const handleDeleteMember = (memberId: string, memberName: string) => {
    deleteMemberMutation.mutate(memberId, {
      onSuccess: () => {
        toast({
          title: "Member Removed",
          description: `${memberName} was removed from your household.`
        });
        refetch();
      },
      onError: (err: any) => {
        toast({
          title: "Error",
          description: err.message || "Failed to remove member.",
          variant: "destructive"
        });
      }
    });
  };

  const membersList = household?.members || [];
  const separateAccountCount = membersList.filter((m: any) => m.accountType === 'SEPARATE_ACCOUNT').length;
  const managedCount = membersList.filter((m: any) => m.accountType === 'MANAGED_DEPENDENT').length;

  return (
    <MemberLayout>
      <div className="w-full space-y-6 sm:space-y-7 pt-4 sm:pt-6 lg:pt-8 pb-16 flex flex-col min-h-[calc(100vh-10rem)]">
        
        {/* Header & Page Banner */}
        <div className="relative overflow-hidden rounded-2xl sm:rounded-3xl bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white shadow-sm border border-indigo-500/20 p-5 sm:p-6 lg:p-7">
          {/* Subtle Ambient Glows and Pattern */}
          <div className="absolute inset-0 bg-[radial-gradient(#6366f1_1px,transparent_1px)] [background-size:24px_24px] opacity-15 pointer-events-none" />
          <div className="absolute -top-16 -right-16 w-64 h-64 bg-cyan-500/15 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-16 -left-16 w-64 h-64 bg-indigo-600/20 rounded-full blur-3xl pointer-events-none" />

          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-5 sm:gap-6">
            <div className="space-y-3 flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <Badge className="bg-white/10 hover:bg-white/15 text-white border border-white/15 backdrop-blur-md text-xs font-bold uppercase tracking-wider py-1 px-3 rounded-full shadow-none flex items-center gap-1.5 shrink-0">
                  <Shield className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                  Household & Family Plan
                </Badge>
                <span className="inline-flex items-center gap-1.5 text-xs text-cyan-200/90 font-mono font-medium bg-black/30 backdrop-blur-md px-3 py-1 rounded-full border border-white/10 shrink-0">
                  <Sparkles className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                  Household ID: {household?.id ? `HH-${household.id.substring(0, 8).toUpperCase()}` : 'Loading...'}
                </span>
              </div>

              <h1 className="text-xl sm:text-2xl lg:text-3xl font-extrabold tracking-tight text-white leading-tight break-words">
                {household?.name || "Family Household Membership"}
              </h1>

              <p className="text-xs sm:text-sm text-slate-300 font-normal leading-relaxed max-w-3xl">
                Manage family members under your household plan. Each member can choose to have a separate account with their own credentials or be a managed dependent profile.
              </p>
            </div>

            <div className="shrink-0">
              <Button
                onClick={() => setIsAddModalOpen(true)}
                className="w-full sm:w-auto h-11 px-5 sm:px-6 rounded-xl bg-white hover:bg-slate-50 text-primary font-bold text-xs sm:text-sm shadow-md hover:shadow-lg active:scale-[0.99] transition-all gap-2 flex items-center justify-center shrink-0 border-0 cursor-pointer"
              >
                <UserPlus className="w-4 h-4 shrink-0 text-primary" /> Add / Invite Household Member
              </Button>
            </div>
          </div>
        </div>

        {/* Overview Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-slate-900 p-4 sm:p-5 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-xs space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Total Members</span>
              <div className="p-2 bg-primary/10 rounded-xl text-primary flex items-center justify-center shrink-0">
                <Users className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
            </div>
            <p className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight">{isLoading ? "—" : membersList.length}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">Active in household</p>
          </div>

          <div className="bg-white dark:bg-slate-900 p-4 sm:p-5 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-xs space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Separate Accounts</span>
              <div className="p-2 bg-emerald-50 dark:bg-emerald-950/50 rounded-xl text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
                <UserCheck className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
            </div>
            <p className="text-2xl sm:text-3xl font-black text-emerald-600 dark:text-emerald-400 tracking-tight">{isLoading ? "—" : separateAccountCount}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">Independent login accounts</p>
          </div>

          <div className="bg-white dark:bg-slate-900 p-4 sm:p-5 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-xs space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Managed Profiles</span>
              <div className="p-2 bg-blue-50 dark:bg-blue-950/50 rounded-xl text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0">
                <Heart className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
            </div>
            <p className="text-2xl sm:text-3xl font-black text-blue-600 dark:text-blue-400 tracking-tight">{isLoading ? "—" : managedCount}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">Managed household dependents</p>
          </div>

          <div className="bg-white dark:bg-slate-900 p-4 sm:p-5 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-xs space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Household Plan</span>
              <div className="p-2 bg-purple-50 dark:bg-purple-950/50 rounded-xl text-purple-600 dark:text-purple-400 flex items-center justify-center shrink-0">
                <Shield className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
            </div>
            <p className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight">Active Premium</p>
            <p className="text-xs text-emerald-600 dark:text-emerald-400 font-bold flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5" /> All Connections Linked
            </p>
          </div>
        </div>

        {/* Household Member Connection List & Cards */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl sm:rounded-3xl border border-slate-200/80 dark:border-slate-800 shadow-xs p-5 sm:p-6 space-y-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 dark:border-slate-800 pb-4">
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
                <div className="p-1.5 bg-primary/10 rounded-lg text-primary shrink-0">
                  <Users className="w-4 h-4 sm:w-5 sm:h-5" />
                </div>
                Household Connections & Member IDs
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 font-medium leading-relaxed">
                View member IDs, relationship status, and account connections for each household member.
              </p>
            </div>
            <Badge variant="outline" className="font-bold text-xs bg-primary/10 text-primary border-primary/20 px-3 py-1 rounded-full shrink-0 self-start sm:self-auto">
              {membersList.length} Connected Members
            </Badge>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Skeleton className="h-40 w-full rounded-2xl" />
              <Skeleton className="h-40 w-full rounded-2xl" />
            </div>
          ) : membersList.length === 0 ? (
            <div className="text-center py-16 sm:py-20 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl bg-slate-50/50 dark:bg-slate-900/50 space-y-3.5 p-6">
              <Users className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto" />
              <h3 className="text-lg font-bold text-slate-800 dark:text-white">No Household Members Added</h3>
              <p className="text-xs sm:text-sm text-slate-500 max-w-sm mx-auto font-medium leading-relaxed">
                Add your spouse, children, parents, or dependents to form your household membership and manage your care together.
              </p>
              <Button onClick={() => setIsAddModalOpen(true)} className="rounded-xl font-bold bg-primary text-primary-foreground hover:bg-primary/90 mt-2 px-6 h-11">
                <UserPlus className="w-4 h-4 mr-2" /> Add Family Member
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {membersList.map((m: any) => {
                const isHead = m.relationship === 'Head of Household';
                const memberName = `${m.firstName} ${m.lastName}`.trim();
                const memberIdNum = m.memberIdNumber || `SD-MB-${m.id.substring(0, 6).toUpperCase()}`;

                return (
                  <motion.div
                    key={m.id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`p-5 sm:p-6 rounded-2xl border transition-all duration-200 relative overflow-hidden flex flex-col justify-between ${
                      isHead
                        ? "border-primary/40 dark:border-primary/40 bg-gradient-to-br from-primary/[0.04] via-transparent to-primary/[0.02] dark:from-primary/10 dark:to-transparent ring-1 ring-primary/20 shadow-xs"
                        : "border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-slate-300 dark:hover:border-slate-700 shadow-xs hover:shadow-sm"
                    }`}
                  >
                    <div>
                      {/* Top Badges Header */}
                      <div className="flex items-center justify-between gap-2 pb-3.5 border-b border-slate-100 dark:border-slate-800/80">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Badge className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md shadow-none ${
                            isHead
                              ? "bg-primary text-primary-foreground"
                              : "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200"
                          }`}>
                            {m.relationship || "Family Member"}
                          </Badge>

                          <Badge variant="outline" className={`text-[10px] font-semibold px-2 py-0.5 rounded-md ${
                            m.accountType === 'SEPARATE_ACCOUNT'
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800"
                              : "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800"
                          }`}>
                            {m.accountType === 'SEPARATE_ACCOUNT' ? "Separate Account" : "Managed Dependent"}
                          </Badge>
                        </div>

                        <Badge className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full shadow-none ${
                          m.status === 'ACTIVE'
                            ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20"
                            : "bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 animate-pulse"
                        }`}>
                          {m.status === 'ACTIVE' ? "Active" : "Invite Pending"}
                        </Badge>
                      </div>

                      {/* Main Member Details */}
                      <div className="pt-3.5 flex items-start gap-3.5">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary via-indigo-600 to-cyan-500 flex items-center justify-center text-white font-extrabold text-base shadow-xs shrink-0">
                          {m.firstName?.[0]}{m.lastName?.[0]}
                        </div>

                        <div className="min-w-0 flex-1 space-y-1.5">
                          <h3 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white truncate">
                            {memberName}
                          </h3>

                          {/* Member ID Display */}
                          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-50 dark:bg-slate-800/80 border border-slate-200/70 dark:border-slate-700 text-xs font-mono font-bold text-primary">
                            <CreditCard className="w-3.5 h-3.5 text-primary shrink-0" />
                            Member ID: {memberIdNum}
                          </div>

                          <div className="space-y-1 text-xs text-slate-500 dark:text-slate-400 pt-0.5">
                            {m.email && (
                              <div className="flex items-center gap-1.5 truncate">
                                <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                <span className="truncate">{m.email}</span>
                              </div>
                            )}
                            {m.phone && (
                              <div className="flex items-center gap-1.5">
                                <Phone className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                <span>{m.phone}</span>
                              </div>
                            )}
                            {m.dob && (
                              <div className="flex items-center gap-1.5">
                                <Calendar className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                <span>DOB: {m.dob}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Bottom Action Bar */}
                    <div className="mt-5 pt-3.5 border-t border-slate-100 dark:border-slate-800/80 flex items-center justify-between gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedCardMember(m)}
                        className="rounded-xl text-xs font-bold gap-1.5 text-primary border-primary/30 hover:bg-primary/10 h-8 sm:h-9 px-3 cursor-pointer"
                      >
                        <CreditCard className="w-3.5 h-3.5 shrink-0" /> View Member ID Card
                      </Button>

                      {!isHead && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteMember(m.id, memberName)}
                          disabled={deleteMemberMutation.isPending}
                          className="rounded-xl text-xs text-rose-600 hover:text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-950/40 h-8 sm:h-9 px-2.5 cursor-pointer"
                          title="Remove from Household"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Add / Invite Household Member Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:w-full max-w-lg sm:max-w-md max-h-[85vh] sm:max-h-[90vh] overflow-y-auto rounded-2xl sm:rounded-3xl p-5 sm:p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl">
          <DialogHeader className="pr-8 sm:pr-9 text-left space-y-1">
            <DialogTitle className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white flex items-center gap-2">
              <UserPlus className="w-5 h-5 sm:w-6 sm:h-6 text-primary shrink-0" /> Add Household Member
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Add a family member or dependent to your household membership plan.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleAddSubmit} className="space-y-4 pt-1 sm:pt-2">
            {/* Account Type Choice Cards */}
            <div className="space-y-2">
              <Label className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider">Account Choice</Label>
              <RadioGroup value={accountType} onValueChange={(v: any) => setAccountType(v)} className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
                <div
                  onClick={() => setAccountType('SEPARATE_ACCOUNT')}
                  className={`p-3 sm:p-3.5 rounded-2xl border text-left cursor-pointer transition-all flex flex-col justify-between ${
                    accountType === 'SEPARATE_ACCOUNT'
                      ? 'border-primary bg-primary/10 ring-2 ring-primary/20'
                      : 'border-slate-200 dark:border-slate-800 bg-slate-50/50 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-bold text-xs text-slate-900 dark:text-white">Separate Account</span>
                    <RadioGroupItem value="SEPARATE_ACCOUNT" id="opt-separate" />
                  </div>
                  <p className="text-[11px] text-slate-500 leading-tight">
                    Member registers/logs in with their own email credentials.
                  </p>
                </div>

                <div
                  onClick={() => setAccountType('MANAGED_DEPENDENT')}
                  className={`p-3 sm:p-3.5 rounded-2xl border text-left cursor-pointer transition-all flex flex-col justify-between ${
                    accountType === 'MANAGED_DEPENDENT'
                      ? 'border-primary bg-primary/10 ring-2 ring-primary/20'
                      : 'border-slate-200 dark:border-slate-800 bg-slate-50/50 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-bold text-xs text-slate-900 dark:text-white">Managed Dependent</span>
                    <RadioGroupItem value="MANAGED_DEPENDENT" id="opt-managed" />
                  </div>
                  <p className="text-[11px] text-slate-500 leading-tight">
                    Profile managed directly under your primary household account.
                  </p>
                </div>
              </RadioGroup>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">First Name *</Label>
                <Input
                  required
                  placeholder="Jane"
                  value={firstName}
                  onChange={e => setFirstName(e.target.value)}
                  className="rounded-xl mt-1 h-10 text-xs sm:text-sm"
                />
              </div>

              <div>
                <Label className="text-xs font-semibold">Last Name *</Label>
                <Input
                  required
                  placeholder="Smith"
                  value={lastName}
                  onChange={e => setLastName(e.target.value)}
                  className="rounded-xl mt-1 h-10 text-xs sm:text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Relationship *</Label>
                <Select value={relationship} onValueChange={v => setRelationship(v)}>
                  <SelectTrigger className="mt-1 h-10 rounded-xl text-xs sm:text-sm">
                    <SelectValue placeholder="Relationship" />
                  </SelectTrigger>
                  <SelectContent>
                    {RELATIONSHIPS.map(rel => (
                      <SelectItem key={rel} value={rel}>{rel}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs font-semibold">Gender</Label>
                <Select value={gender} onValueChange={v => setGender(v)}>
                  <SelectTrigger className="mt-1 h-10 rounded-xl text-xs sm:text-sm">
                    <SelectValue placeholder="Gender" />
                  </SelectTrigger>
                  <SelectContent>
                    {GENDERS.map(g => (
                      <SelectItem key={g} value={g}>{g}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold">
                Email Address {accountType === 'SEPARATE_ACCOUNT' ? '*' : '(Optional)'}
              </Label>
              <Input
                type="email"
                placeholder="family.member@example.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required={accountType === 'SEPARATE_ACCOUNT'}
                className="rounded-xl mt-1 h-10 text-xs sm:text-sm"
              />
              {accountType === 'SEPARATE_ACCOUNT' && (
                <p className="text-[11px] text-muted-foreground mt-1">
                  An email invite will be sent for separate account sign-up.
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Phone (Optional)</Label>
                <Input
                  placeholder="(555) 000-0000"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  className="rounded-xl mt-1 h-10 text-xs sm:text-sm"
                />
              </div>

              <div>
                <Label className="text-xs font-semibold">Date of Birth (Optional)</Label>
                <Input
                  type="date"
                  value={dob}
                  onChange={e => setDob(e.target.value)}
                  className="rounded-xl mt-1 h-10 text-xs sm:text-sm"
                />
              </div>
            </div>

            <DialogFooter className="pt-3 sm:pt-4 flex flex-row items-center justify-end gap-2.5">
              <Button type="button" variant="outline" onClick={() => setIsAddModalOpen(false)} className="rounded-xl cursor-pointer">
                Cancel
              </Button>
              <Button type="submit" disabled={addMemberMutation.isPending} className="rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-bold cursor-pointer">
                {addMemberMutation.isPending ? "Adding..." : "Add Member"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Digital Member ID Card Modal */}
      {selectedCardMember && (
        <Dialog open={!!selectedCardMember} onOpenChange={() => setSelectedCardMember(null)}>
          <DialogContent className="w-[calc(100vw-2rem)] sm:w-full max-w-md max-h-[90vh] overflow-y-auto rounded-2xl sm:rounded-3xl p-4 sm:p-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl">
            <DialogHeader className="pr-8 sm:pr-9 text-left">
              <div className="flex flex-wrap items-center gap-2 justify-between">
                <DialogTitle className="text-base sm:text-lg font-bold text-slate-900 dark:text-white">
                  <span>Digital Member ID Card</span>
                </DialogTitle>
                <Badge className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 font-bold text-[10px] px-2.5 py-0.5 rounded-full shrink-0">
                  VERIFIED MEMBER
                </Badge>
              </div>
            </DialogHeader>

            {/* Official Digital Card Preview */}
            <div className="my-2.5 sm:my-3 p-4 sm:p-5 lg:p-6 rounded-2xl sm:rounded-3xl bg-gradient-to-br from-blue-700 via-indigo-700 to-slate-900 text-white shadow-xl relative overflow-hidden border border-white/20 divide-y divide-white/15">
              {/* Card Ambient Glows */}
              <div className="absolute top-0 right-0 w-44 h-44 bg-cyan-400/20 rounded-full blur-2xl pointer-events-none -mr-10 -mt-10" />
              <div className="absolute bottom-0 left-0 w-36 h-36 bg-indigo-500/20 rounded-full blur-xl pointer-events-none -ml-8 -mb-8" />

              <div className="relative z-10 flex items-center justify-between pb-3 sm:pb-3.5 gap-3">
                <div className="min-w-0 flex-1">
                  <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-cyan-200 block">StraightDoc Transparent Health</span>
                  <span className="text-sm sm:text-base lg:text-lg font-bold tracking-tight text-white truncate block mt-0.5">{household?.name || "Household Member Card"}</span>
                </div>
                <div className="p-1.5 sm:p-2 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 shrink-0">
                  <QrCode className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-white" />
                </div>
              </div>

              <div className="relative z-10 py-3 sm:py-3.5 space-y-1.5 sm:space-y-2">
                <span className="text-[9px] sm:text-[10px] uppercase font-bold text-cyan-200/90 tracking-wider block">Member Name & Connection</span>
                <h3 className="text-lg sm:text-xl lg:text-2xl font-black tracking-tight text-white leading-tight break-words">
                  {selectedCardMember.firstName} {selectedCardMember.lastName}
                </h3>
                <div className="pt-0.5">
                  <span className="inline-block px-2.5 sm:px-3 py-0.5 sm:py-1 rounded-full bg-white/15 backdrop-blur-md text-[11px] sm:text-xs font-semibold text-white border border-white/20">
                    {selectedCardMember.relationship} ({selectedCardMember.accountType === 'SEPARATE_ACCOUNT' ? 'Separate Account' : 'Managed Dependent'})
                  </span>
                </div>
              </div>

              <div className="relative z-10 pt-3 sm:pt-3.5 grid grid-cols-2 gap-3 sm:gap-4 text-xs">
                <div className="min-w-0">
                  <span className="text-[9px] sm:text-[10px] font-bold text-cyan-200/90 block uppercase tracking-wider mb-1">Member ID Number</span>
                  <span className="font-mono font-black text-xs sm:text-sm lg:text-base tracking-wider text-white block truncate">
                    {selectedCardMember.memberIdNumber || `SD-MB-${selectedCardMember.id.substring(0, 6).toUpperCase()}`}
                  </span>
                </div>

                <div className="min-w-0">
                  <span className="text-[9px] sm:text-[10px] font-bold text-cyan-200/90 block uppercase tracking-wider mb-1">Plan Status</span>
                  <span className="font-bold text-emerald-300 text-xs sm:text-sm flex items-center gap-1.5 truncate">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" /> Active Household
                  </span>
                </div>
              </div>
            </div>

            <DialogFooter className="pt-1.5 sm:pt-2">
              <Button onClick={() => setSelectedCardMember(null)} className="w-full h-10 sm:h-11 rounded-xl bg-slate-900 text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100 font-bold text-xs sm:text-sm cursor-pointer shadow-sm">
                Close Member Card
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

    </MemberLayout>
  );
}
